import { FC, useState } from "react";
import {
  Button,
  Card,
  Copy,
  Divider,
  Flex,
  Modal,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useAppTranslation, useBackToShop } from "@/utils";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { CuToman, InfoCircleFilled } from "@payping/icons";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import Loading from "@/pages/loading";
import HowToWriteChequeDrawer from "@/Components/how-to-write-cheque-drawer";
import PayGetOrderLayout from "../Layout";
import PayUnAvailable from "../../Unavailable";

interface ChequeSubmitProps {
  trackingCode: string;
  afterSuccess: () => void;
}

const GetOrderChequeSubmit: FC<ChequeSubmitProps> = ({ afterSuccess, trackingCode }) => {
  const { t } = useAppTranslation();
  const { shopName } = useBackToShop();
  const [showHowToWriteCheque, setShowHowToWriteCheque] = useState(false);
  const [finaCheckModalState, setFinalCheckModalState] = useState(false);

  const { loading, error, value, retry } = useMessagedAsyncRetry(
    async () => {
      const res = await bnplConsumerApis.getChequeInfo(trackingCode, { isRaw: true } as SDKRequestInit);

      return res;
    },
    [trackingCode],
    {
      success: false,
      error: true,
    },
  );

  const [ConfirmCheque, requestConfirmCheque] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.confirmChequeInfo({ trackingCode }, {
        isRaw: true,
      } as SDKRequestInit);
      afterSuccess();
      return res;
    } catch (e: any) {
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    }
  }, [afterSuccess, trackingCode]);

  if (loading || error?.name === "AuthError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  if (error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            retry();
          }}
          onRetryLoading={loading}
        />
      </PayGetOrderLayout>
    );
  }

  return (
    <PayGetOrderLayout showHeaders>
      <Flex vertical className="tw:h-full tw:w-full tw:pb-4! tw:pt-8!" gap={"large"}>
        <Flex vertical className="tw:w-full tw:px-4!" gap={"middle"}>
          {/* <Alert title={t("pay.getOrder.steps.chequeSubmit.alert")} type="warning" showIcon /> */}
          <Flex gap={"small"} vertical>
            <Typography.Title level={5} className="tw:m-0! tw:font-semibold">
              {t("pay.getOrder.steps.chequeSubmit.title")}
            </Typography.Title>
            <Typography.Paragraph className="tw:m-0!">
              {t("pay.getOrder.steps.chequeSubmit.description", { shopName })}
            </Typography.Paragraph>
          </Flex>
        </Flex>
        <div className="tw:px-4!">
          <Card size="small">
            <Flex vertical className="tw:w-full">
              <Flex align="center" className="tw:w-full" gap={"middle"} justify="space-between">
                <Typography.Text type="secondary" className="tw:m-0!">
                  {t("pay.getOrder.steps.chequeSubmit.infoTable.inTheName")}
                </Typography.Text>
                <Typography.Text className="tw:m-0!">{value?.recipientName ?? ""}</Typography.Text>
              </Flex>
              <Divider className="tw:mb-4! tw:mt-2!" />
              <Flex align="center" className="tw:w-full" gap={"middle"} justify="space-between">
                <Typography.Text type="secondary" className="tw:m-0!">
                  {t("common.nationalCode")}
                </Typography.Text>
                <Flex align="center" gap={4}>
                  <Typography.Text className="tw:m-0!">{value?.recipientIdentifier ?? ""}</Typography.Text>
                  <Copy showIcon text={value?.recipientIdentifier ?? ""}></Copy>
                </Flex>
              </Flex>
              <Divider className="tw:mb-4! tw:mt-2!" />
              <Flex align="center" className="tw:w-full" gap={"middle"} justify="space-between">
                <Typography.Text type="secondary" className="tw:m-0!">
                  {t("pay.getOrder.steps.chequeSubmit.infoTable.chequeAmount")}
                </Typography.Text>
                <Flex align="center" gap={4}>
                  <Typography.Text className="tw:m-0!">
                    {formatLocalizedNumber({ value: (value?.amountRial ?? 0) / 10 })}
                  </Typography.Text>
                  <CuToman width={24} />
                </Flex>
              </Flex>
              <Divider className="tw:mb-4! tw:mt-2!" />
              <Flex align="center" className="tw:w-full" gap={"middle"} justify="space-between">
                <Typography.Text type="secondary" className="tw:m-0!">
                  {t("pay.getOrder.steps.chequeSubmit.infoTable.chequeDate")}
                </Typography.Text>
                <Typography.Text className="tw:m-0!">{value?.duedate}</Typography.Text>
              </Flex>
              <Divider className="tw:mb-4! tw:mt-2!" />
              <Flex align="center" className="tw:w-full" gap={"middle"} justify="space-between">
                <Typography.Text type="secondary" className="tw:m-0!">
                  {t("pay.getOrder.steps.chequeSubmit.infoTable.reason")}
                </Typography.Text>
                <Typography.Text className="tw:m-0!">
                  {t("pay.getOrder.steps.chequeSubmit.infoTable.reasonText")}
                </Typography.Text>
              </Flex>
            </Flex>
          </Card>
        </div>

        <div className="tw:px-4!">
          <Card
            size="small"
            className="tw:border tw:border-solid tw:border-color-border-secondary tw:bg-color-bg-layout"
          >
            {t("pay.getOrder.steps.chequeSubmit.alert2")}
          </Card>
        </div>

        <Flex vertical gap={"middle"} className="tw:flex-1">
          <div className="tw:h-1.5 tw:w-full tw:bg-color-fill-tertiary" />

          <Flex vertical className="tw:px-4!" justify="start" gap={"small"}>
            <Typography.Text>{t("pay.getOrder.steps.chequeSubmit.confirmText")}</Typography.Text>
            <div>
              <Button
                block={false}
                type="link"
                className="tw:h-auto! tw:p-0!"
                onClick={() => {
                  setShowHowToWriteCheque(true);
                }}
              >
                {t("howToWriteCheckDrawer.title")}
              </Button>
            </div>
            <HowToWriteChequeDrawer
              open={showHowToWriteCheque}
              onClose={() => {
                setShowHowToWriteCheque(false);
              }}
              data={{
                amountRial: value?.amountRial,
                duedate: value?.duedate,
                recipientIdentifier: value?.recipientIdentifier,
                recipientName: value?.recipientName,
                isLegal: value?.recipientIsLegal ?? false,
              }}
            />
          </Flex>

          <div className="tw:mt-auto tw:px-4!">
            <Button
              block
              type="primary"
              loading={ConfirmCheque.loading}
              onClick={() => {
                setFinalCheckModalState(true);
              }}
            >
              {t("pay.getOrder.steps.chequeSubmit.submitted")}
            </Button>
          </div>
          {finaCheckModalState ? (
            <Modal
              closable={false}
              maskClosable
              title={
                <Flex gap={"middle"}>
                  <InfoCircleFilled width={20} height={20} className="tw:text-color-warning" />
                  <Typography.Text>
                    <strong>{t("pay.getOrder.steps.chequeSubmit.finalCheckModal.title")}</strong>
                  </Typography.Text>
                </Flex>
              }
              open={finaCheckModalState}
              onCancel={() => {
                setFinalCheckModalState(false);
              }}
              footer={
                <Button
                  loading={ConfirmCheque.loading}
                  onClick={() => {
                    if (ConfirmCheque.loading) {
                      return;
                    }
                    requestConfirmCheque();
                  }}
                  color="primary"
                  variant="solid"
                >
                  {t("common.confirmed")}
                </Button>
              }
            >
              <div className="tw:mr-9!">
                <Typography.Text>{t("pay.getOrder.steps.chequeSubmit.finalCheckModal.content")}</Typography.Text>
              </div>
            </Modal>
          ) : null}
        </Flex>
      </Flex>
    </PayGetOrderLayout>
  );
};

export default GetOrderChequeSubmit;
